import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bot,
  RefreshCw,
  ThumbsUp,
  ThumbsDown,
  Minus,
  CheckCircle,
  XCircle,
  Lightbulb,
  ArrowRight,
} from "lucide-react";
import api from "../api/client";
import ScoreRing from "./ScoreRing";

const SENTIMENT_ICONS = {
  positive: { icon: ThumbsUp, color: "text-green-400", bg: "bg-green-900/20" },
  neutral: { icon: Minus, color: "text-gray-400", bg: "bg-gray-800" },
  negative: { icon: ThumbsDown, color: "text-red-400", bg: "bg-red-900/20" },
  not_mentioned: { icon: XCircle, color: "text-gray-600", bg: "bg-dark-50" },
};

export default function GeoMonitor({ selectedSite }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [loadingRecs, setLoadingRecs] = useState(false);

  const fetchData = () => {
    if (!selectedSite) return;
    setLoading(true);
    api
      .get(`/websites/${selectedSite.id}/geo/`)
      .then((res) => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, [selectedSite]);

  const runAudit = () => {
    setRunning(true);
    api
      .post(`/websites/${selectedSite.id}/geo/run/`)
      .then(() => setTimeout(fetchData, 5000))
      .finally(() => setRunning(false));
  };

  const loadRecommendations = () => {
    setLoadingRecs(true);
    api
      .get(`/websites/${selectedSite.id}/geo/recommendations/`)
      .then((res) => setRecommendations(res.data.recommendations || []))
      .catch(() => setRecommendations([]))
      .finally(() => setLoadingRecs(false));
  };

  if (!selectedSite)
    return <div className="text-gray-500 text-center py-20">Select a website</div>;

  const score = data?.score;
  const checks = data?.recent_checks || [];
  const mentionCount = checks.filter((c) => c.mentions_brand).length;
  const competitorMentions = {};
  checks.forEach((c) => {
    (c.mentions_competitor || []).forEach((comp) => {
      competitorMentions[comp] = (competitorMentions[comp] || 0) + 1;
    });
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Bot size={20} className="text-copper" /> GEO Monitor
        </h2>
        <div className="flex gap-2">
          <button
            onClick={loadRecommendations}
            disabled={loadingRecs}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-dark-300 border border-dark-50 rounded-lg text-gray-300 hover:text-white disabled:opacity-50"
          >
            <Lightbulb size={12} className={loadingRecs ? "animate-pulse" : ""} />
            Get Recommendations
          </button>
          <button
            onClick={runAudit}
            disabled={running}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-copper/20 text-copper rounded-lg hover:bg-copper/30 disabled:opacity-50"
          >
            <RefreshCw size={12} className={running ? "animate-spin" : ""} />
            Run GEO Audit
          </button>
        </div>
      </div>

      {loading ? (
        <div className="animate-pulse space-y-4">
          <div className="h-32 bg-dark-300 rounded-xl" />
          <div className="h-64 bg-dark-300 rounded-xl" />
        </div>
      ) : (
        <>
          {/* Score & Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-dark-300 border border-dark-50 rounded-xl p-5 flex flex-col items-center justify-center">
              <ScoreRing score={score?.overall_score || 0} size={80} strokeWidth={5} label="GEO Score" />
              <div className="text-xs text-gray-500 mt-2">AI Discoverability</div>
            </div>

            <div className="bg-dark-300 border border-dark-50 rounded-xl p-5">
              <div className="text-sm text-gray-400 mb-2">AI Mentions</div>
              <div className="text-3xl font-mono font-bold text-copper">
                {mentionCount}/{checks.length}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                queries mention your brand ({score?.citation_rate?.toFixed(1) || 0}%)
              </div>
            </div>

            <div className="bg-dark-300 border border-dark-50 rounded-xl p-5">
              <div className="text-sm text-gray-400 mb-2">Competitors Cited</div>
              <div className="space-y-1.5 mt-2">
                {Object.entries(competitorMentions).length > 0 ? (
                  Object.entries(competitorMentions)
                    .sort((a, b) => b[1] - a[1])
                    .slice(0, 4)
                    .map(([comp, count]) => (
                      <div key={comp} className="flex items-center justify-between text-xs">
                        <span className="text-gray-300 truncate">{comp}</span>
                        <span className="text-red-400 font-mono">{count}x</span>
                      </div>
                    ))
                ) : (
                  <div className="text-xs text-gray-500">No competitor mentions detected</div>
                )}
              </div>
            </div>
          </div>

          {/* Recent Checks Table */}
          <div className="bg-dark-300 border border-dark-50 rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-dark-50">
              <h3 className="text-sm font-semibold text-gray-300">Recent Citation Checks</h3>
            </div>
            {checks.length === 0 ? (
              <div className="text-gray-500 text-center py-12 text-sm">
                No checks yet — click "Run GEO Audit" to start
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-gray-500 border-b border-dark-50">
                    <th className="text-left py-2.5 px-4 font-medium">Query</th>
                    <th className="text-left py-2.5 px-3 font-medium">Platform</th>
                    <th className="py-2.5 px-3 font-medium">Mentioned?</th>
                    <th className="text-left py-2.5 px-3 font-medium">Competitors</th>
                    <th className="py-2.5 px-3 font-medium">Sentiment</th>
                  </tr>
                </thead>
                <tbody>
                  {checks.map((c) => {
                    const sentConf = SENTIMENT_ICONS[c.sentiment] || SENTIMENT_ICONS.not_mentioned;
                    const SentIcon = sentConf.icon;
                    return (
                      <tr key={c.id} className="border-b border-dark-50/50 hover:bg-dark-50/30">
                        <td className="py-2.5 px-4 text-gray-300 max-w-xs truncate">{c.query}</td>
                        <td className="py-2.5 px-3 text-xs text-gray-400 capitalize">{c.ai_platform}</td>
                        <td className="py-2.5 px-3 text-center">
                          {c.mentions_brand ? (
                            <CheckCircle size={14} className="text-green-400 inline" />
                          ) : (
                            <XCircle size={14} className="text-gray-600 inline" />
                          )}
                        </td>
                        <td className="py-2.5 px-3 text-xs text-gray-400">
                          {(c.mentions_competitor || []).join(", ") || "—"}
                        </td>
                        <td className="py-2.5 px-3 text-center">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${sentConf.bg} ${sentConf.color}`}>
                            <SentIcon size={10} />
                            {c.sentiment}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Recommendations */}
          {recommendations.length > 0 && (
            <div className="bg-dark-300 border border-dark-50 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-gray-300 mb-3 flex items-center gap-2">
                <Lightbulb size={14} className="text-copper" /> GEO Improvement Recommendations
              </h3>
              <div className="space-y-2">
                {recommendations.map((rec, i) => {
                  const recLower = (rec || "").toLowerCase();
                  let linkTo = null;
                  let linkLabel = null;
                  if (recLower.includes("schema") || recLower.includes("structured data")) {
                    linkTo = "/seo-optimizer"; linkLabel = "SEO Optimizer";
                  } else if (recLower.includes("service page") || recLower.includes("landing page") || recLower.includes("create page")) {
                    linkTo = "/landing-pages"; linkLabel = "Landing Pages";
                  } else if (recLower.includes("backlink") || recLower.includes("citation") || recLower.includes("directory")) {
                    linkTo = "/backlinks"; linkLabel = "Backlinks";
                  } else if (recLower.includes("content") || recLower.includes("blog")) {
                    linkTo = "/content"; linkLabel = "Content";
                  } else if (recLower.includes("internal link")) {
                    linkTo = "/internal-links"; linkLabel = "Internal Links";
                  }
                  return (
                    <div key={i} className="flex items-start gap-2 text-sm">
                      <span className="text-copper mt-0.5 font-bold shrink-0">{i + 1}.</span>
                      <span className="text-gray-300 flex-1">{rec}</span>
                      {linkTo && (
                        <button
                          onClick={() => navigate(linkTo)}
                          className="flex items-center gap-1 text-[10px] text-copper hover:text-copper/80 shrink-0 mt-0.5"
                        >
                          {linkLabel} <ArrowRight size={10} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
